import { appPath } from "../appPath";
import type {
  CreateOrderInput,
  InquiryResultResponse,
  PaymentRecord,
  QueryOrder,
  ReportExportSummary,
  CustomerIdentity,
  CustomerOrderQuote,
  CustomerProductCatalog,
  FirmAccount,
  WalletSummary,
  SupportTicket,
  SupportTicketDetail,
} from "../types/api";
import type { PersonalRecharge, PersonalWallet, RechargeChannel } from "../customer-personal/BalanceAccount";
import type { RewardSummary } from "../customer-personal/MyRewards";

interface BackendOrder {
  id: string;
  product_code: QueryOrder["productCode"];
  subject_summary: string;
  status: string;
  sale_price_snapshot: number;
  billing_policy_code?: QueryOrder["billingPolicyCode"];
  billing_status?: QueryOrder["billingStatus"];
  created_at?: string;
  inquiry_payload?: CreateOrderInput["inquiryPayload"];
  report_export?: BackendReportExport | null;
}

interface BackendReportExport {
  id: string;
  status: ReportExportSummary["status"];
  filename: string;
  result_number: string;
  result_version: number;
  can_save: boolean;
  error_summary?: string | null;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type LostContactOutcome =
  | "PENDING"
  | "CONNECTED"
  | "NO_ANSWER"
  | "REJECTED"
  | "INVALID_NUMBER"
  | "OTHER";

export interface LostContactContactRecord {
  outcome: LostContactOutcome;
  note: string;
  createdAt: string | null;
}

export interface LostContactBinding {
  id: string;
  status: string;
  virtualNumber: string | null;
  seatNumber: string;
  createdAt: string | null;
  expiresAt: string | null;
  latestContactRecord: LostContactContactRecord;
  contactRecordHistory: LostContactContactRecord[];
}

export interface LostContactRepairMember {
  id: string;
  position: number;
  name: string;
  idCard: string;
  phone: string | null;
  repairStatus: string;
  contactCount: number | null;
  caseInfo: {
    branchName: string | null; accountManager: string | null;
    loanPrincipal: string | null; loanInterest: string | null;
    loanDate: string | null; loanExpirationDate: string | null; legacyPhone: string | null;
  };
  bindings: LostContactBinding[];
}

export interface LostContactRepairBatch {
  id: string;
  status: string;
  memberCount: number;
  errorSummary: string | null;
  createdAt: string;
  submittedAt: string | null;
  members?: LostContactRepairMember[];
}

export interface LostContactOutboundProfile {
  id: string;
  name: string;
  seatNumber: string;
  tag1: string | null;
  tag2: string | null;
}

export interface LostContactManagedOutboundProfile extends LostContactOutboundProfile {
  telX: string;
  active: boolean;
  pendingAction: "DISABLE" | "DELETE" | null;
  carrierVerifiedAt: string | null;
  carrierValidationError: string | null;
  updatedAt: string;
}

export type LostContactOutboundProfileInput = {
  name: string; seatNumber: string; telX: string; tag1?: string | null; tag2?: string | null; active: boolean;
};

export interface LostContactUploadPreflight {
  totalRows: number;
  validRows: number;
  errors: Array<{ row: number; field: string; message: string }>;
}

export interface OrderAuthorizationMaterial {
  id: string;
  name: string;
  content_type: string;
  size_bytes: number;
  expires_at: string | null;
  state: "DRAFT" | "ATTACHED" | "EXPIRED";
  download_url: string;
}

let csrfToken: string | null = null;
let customerSessionInvalidHandler: (() => void) | null = null;

const CUSTOMER_SESSION_INVALID = "CUSTOMER_SESSION_INVALID";

export function setCustomerSessionInvalidHandler(handler: (() => void) | null): void {
  customerSessionInvalidHandler = handler;
}

export interface PlatformLoginResult {
  destination: "ADMIN" | "LEGAL" | "FINANCIAL";
  access_token: string | null;
  csrf_token: string | null;
  role: "ORGANIZATION_ADMIN" | "LAWYER" | null;
  organization_id: string | null;
  organization_name: string | null;
  organization_type: "LAW_FIRM" | "FINANCIAL_INSTITUTION" | "OTHER" | null;
  must_change_password: boolean;
}

export interface LawyerRegistrationInput {
  mobile: string;
  code: string;
  name: string;
  accepted_terms: true;
  invitation_code?: string | null;
}

export interface LawyerRegistrationResult {
  registration_status: "CREATED" | "ALREADY_REGISTERED";
  login_url: string;
  login_account: string;
  password: string;
  lawyer_number: string;
}

export type PersonalLawyerVerificationStatus =
  | "NOT_SUBMITTED"
  | "PENDING"
  | "APPROVED"
  | "REJECTED";

export interface PersonalLawyerVerificationMaterial {
  id: string;
  kind: "IDENTITY_FRONT" | "IDENTITY_BACK" | "PRACTICE_CERTIFICATE";
  name: string;
  content_type: "image/jpeg" | "image/png";
  review_url: string;
}

export interface PersonalLawyerProfile {
  lawyer_number: string;
  lawyer_name: string;
  mobile: string;
  law_firm_name: string | null;
  status: PersonalLawyerVerificationStatus;
  identity_number: string | null;
  practice_certificate_number: string | null;
  rejection_reason: string | null;
  materials: PersonalLawyerVerificationMaterial[];
}

export interface PersonalLawyerVerificationInput {
  lawyerName: string;
  lawFirmName: string;
  identityNumber: string;
  practiceCertificateNumber: string;
  identityFront: File;
  identityBack: File;
  practiceCertificate: File;
}

export function setCsrfToken(value: string | null): void {
  csrfToken = value;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const mutation = !["GET", "HEAD", "OPTIONS"].includes(method);
  const isFormData = init?.body instanceof FormData;
  const response = await fetch(appPath(`/api${path}`), {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(!isFormData ? { "Content-Type": "application/json" } : {}),
      ...(mutation && csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      ...init?.headers,
    },
  });
  let payload: unknown = null;
  if (response.status !== 204) {
    const body = await response.text();
    if (body) {
      try {
        payload = JSON.parse(body);
      } catch {
        if (response.ok) {
          throw new ApiError("RESPONSE_INVALID", "服务响应异常，请稍后重试", response.status);
        }
      }
    }
  }
  if (!response.ok) {
    const responseBody = typeof payload === "object" && payload !== null
      ? payload as Record<string, unknown>
      : null;
    const detail = typeof responseBody?.detail === "object" && responseBody.detail !== null
      ? responseBody.detail as Record<string, unknown>
      : responseBody;
    const code = typeof detail?.code === "string" ? detail.code : "REQUEST_FAILED";
    const validation = typeof detail?.error === "object" && detail.error !== null
      ? detail.error as Record<string, unknown>
      : null;
    const message = typeof detail?.message === "string"
      ? detail.message
      : code === "FINANCIAL_WORKBOOK_INVALID" && typeof validation?.message === "string"
        ? validation.message
        : (code === CUSTOMER_SESSION_INVALID ? "登录已失效，请重新登录。" : "请求失败，请稍后重试");
    const error = new ApiError(
      code,
      message,
      response.status,
    );
    if (error.status === 401 && error.code === CUSTOMER_SESSION_INVALID && path !== "/customer-auth/logout") {
      customerSessionInvalidHandler?.();
    }
    throw error;
  }
  return payload as T;
}

export function customerLogin(mobile: string, password: string): Promise<CustomerIdentity> {
  return request<CustomerIdentity>("/customer-auth/login", {
    method: "POST",
    body: JSON.stringify({ mobile, password }),
  }).then((identity) => { setCsrfToken(identity.csrf_token); return identity; });
}

export function sendLawyerRegistrationCode(mobile: string): Promise<void> {
  return request<void>("/lawyer-registration/sms-codes", {
    method: "POST",
    body: JSON.stringify({ mobile }),
  });
}

export function registerLawyer(
  input: LawyerRegistrationInput,
): Promise<LawyerRegistrationResult> {
  return request<LawyerRegistrationResult>("/lawyer-registration", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function getPersonalLawyerProfile(): Promise<PersonalLawyerProfile> {
  return request<PersonalLawyerProfile>("/personal-lawyer/profile");
}

export function getPersonalLawyerInvitation(): Promise<{ code: string; url: string }> {
  return request("/personal-lawyer/invitation");
}

export function getPersonalLawyerRewards(invitePage = 1, recordPage = 1): Promise<RewardSummary> {
  return request<RewardSummary>(`/personal-lawyer/rewards?invite_page=${invitePage}&record_page=${recordPage}`).then((summary) => ({
    ...summary,
    available_reward_balance: Number(summary.available_reward_balance),
    direct_invites: summary.direct_invites.map((item) => ({
      ...item,
      contribution_amount: Number(item.contribution_amount),
    })),
    records: summary.records.map((item) => ({ ...item, amount: Number(item.amount) })),
  }));
}

export function submitPersonalLawyerVerification(
  input: PersonalLawyerVerificationInput,
): Promise<PersonalLawyerProfile> {
  const body = new FormData();
  body.set("lawyer_name", input.lawyerName);
  body.set("law_firm_name", input.lawFirmName);
  body.set("identity_number", input.identityNumber);
  body.set("practice_certificate_number", input.practiceCertificateNumber);
  body.set("identity_front", input.identityFront);
  body.set("identity_back", input.identityBack);
  body.set("practice_certificate", input.practiceCertificate);
  return request<PersonalLawyerProfile>("/personal-lawyer/verification", {
    method: "POST",
    body,
  });
}

export function customerTestLogin(): Promise<CustomerIdentity> {
  return request<CustomerIdentity>("/customer-auth/test-login", {
    method: "POST",
  }).then((identity) => {
    setCsrfToken(identity.csrf_token);
    return identity;
  });
}

export function customerInstitutionTestLogin(): Promise<CustomerIdentity> {
  return request<CustomerIdentity>("/customer-auth/test-login/institution", {
    method: "POST",
  }).then((identity) => {
    setCsrfToken(identity.csrf_token);
    return identity;
  });
}

export function platformLogin(account: string, password: string): Promise<PlatformLoginResult> {
  return request<PlatformLoginResult>("/platform-auth/login", {
    method: "POST",
    body: JSON.stringify({ account, password }),
  }).then((identity) => {
    if (identity.destination === "ADMIN" && identity.access_token && typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("platform_admin_token", identity.access_token);
    }
    if (identity.destination !== "ADMIN") setCsrfToken(identity.csrf_token);
    return identity;
  });
}

export function platformTestLogin(): Promise<PlatformLoginResult> {
  return request<PlatformLoginResult>("/platform-auth/test-login", {
    method: "POST",
  }).then((identity) => {
    if (identity.destination === "ADMIN" && identity.access_token && typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("platform_admin_token", identity.access_token);
    }
    return identity;
  });
}

export async function customerLogout(): Promise<void> {
  try {
    await request<void>("/customer-auth/logout", { method: "POST" });
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;
  } finally {
    setCsrfToken(null);
  }
}

export function getCustomerSession(): Promise<CustomerIdentity> {
  return request<CustomerIdentity>("/customer-auth/session").then((identity) => { setCsrfToken(identity.csrf_token); return identity; });
}

export function listLostContactRepairBatches(page = 1): Promise<{
  page: number;
  pageSize: number;
  items: LostContactRepairBatch[];
}> {
  return request(`/customer/lost-contact-repairs?page=${page}`);
}

export function getLostContactRepairBatch(batchId: string): Promise<LostContactRepairBatch> {
  return request(`/customer/lost-contact-repairs/${encodeURIComponent(batchId)}`);
}

export function createLostContactRepairBatch(
  rows: Array<{ name: string; idCard: string; phone?: string }>,
  idempotencyKey: string,
): Promise<LostContactRepairBatch> {
  return request("/customer/lost-contact-repairs", {
    method: "POST",
    body: JSON.stringify({ rows, idempotencyKey }),
  });
}

export function uploadLostContactRepairBatch(
  file: File,
  idempotencyKey: string,
): Promise<LostContactRepairBatch> {
  const body = new FormData();
  body.append("file", file);
  body.append("idempotency_key", idempotencyKey);
  return request("/customer/lost-contact-repairs/upload", { method: "POST", body });
}

export function validateLostContactRepairUpload(file: File): Promise<LostContactUploadPreflight> {
  const body = new FormData();
  body.append("file", file);
  return request("/customer/lost-contact-repairs/upload/validate", { method: "POST", body });
}

export function submitLostContactRepairBatch(batchId: string): Promise<LostContactRepairBatch> {
  return request(`/customer/lost-contact-repairs/${encodeURIComponent(batchId)}/submit`, {
    method: "POST",
  });
}

export function listLostContactOutboundProfiles(): Promise<{ items: LostContactOutboundProfile[] }> {
  return request("/customer/lost-contact-repairs/outbound-profiles");
}

export function listManagedLostContactOutboundProfiles(): Promise<{ items: LostContactManagedOutboundProfile[] }> {
  return request("/customer/lost-contact-repairs/outbound-profiles/manage");
}

export function createLostContactOutboundProfile(input: LostContactOutboundProfileInput): Promise<LostContactManagedOutboundProfile> {
  return request("/customer/lost-contact-repairs/outbound-profiles", { method: "POST", body: JSON.stringify(input) });
}

export function updateLostContactOutboundProfile(id: string, input: LostContactOutboundProfileInput): Promise<LostContactManagedOutboundProfile> {
  return request(`/customer/lost-contact-repairs/outbound-profiles/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function disableLostContactOutboundProfile(id: string): Promise<LostContactManagedOutboundProfile> {
  return request(`/customer/lost-contact-repairs/outbound-profiles/${encodeURIComponent(id)}/disable`, { method: "POST" });
}

export function enableLostContactOutboundProfile(id: string): Promise<LostContactManagedOutboundProfile> {
  return request(`/customer/lost-contact-repairs/outbound-profiles/${encodeURIComponent(id)}/enable`, { method: "POST" });
}

export function cancelLostContactOutboundProfileAction(id: string): Promise<LostContactManagedOutboundProfile> {
  return request(`/customer/lost-contact-repairs/outbound-profiles/${encodeURIComponent(id)}/cancel-pending-action`, { method: "POST" });
}

export function deleteLostContactOutboundProfile(id: string): Promise<{
  deleted: boolean;
  profile: LostContactManagedOutboundProfile | null;
}> {
  return request(`/customer/lost-contact-repairs/outbound-profiles/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function lookupLostContactMemberContacts(memberId: string): Promise<{
  contacts: Array<{ index: number; label: string }>;
}> {
  return request(`/customer/lost-contact-repairs/members/${encodeURIComponent(memberId)}/contacts`, {
    method: "POST",
  });
}

export function bindLostContactMember(
  memberId: string,
  outboundProfileId: string,
  contactIndex: number,
): Promise<LostContactBinding> {
  return request(`/customer/lost-contact-repairs/members/${encodeURIComponent(memberId)}/bindings`, {
    method: "POST",
    body: JSON.stringify({ outboundProfileId, contactIndex }),
  });
}

export function unbindLostContactMember(bindingId: string): Promise<LostContactBinding> {
  return request(`/customer/lost-contact-repairs/bindings/${encodeURIComponent(bindingId)}`, {
    method: "DELETE",
  });
}

export function saveLostContactBindingContactRecord(
  bindingId: string,
  outcome: LostContactOutcome,
  note: string,
): Promise<LostContactBinding> {
  return request(
    `/customer/lost-contact-repairs/bindings/${encodeURIComponent(bindingId)}/contact-records`,
    { method: "POST", body: JSON.stringify({ outcome, note }) },
  );
}

export function getCustomerProducts(): Promise<CustomerProductCatalog> {
  return request<CustomerProductCatalog>("/customer/products", { cache: "no-store" });
}

export function changeCustomerPassword(currentPassword: string, newPassword: string): Promise<void> {
  return request<void>("/customer-auth/change-password", {
    method: "POST",
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

export function getWallet(): Promise<WalletSummary> {
  return request<WalletSummary>("/wallet").then((wallet) => ({
    ...wallet,
    firm_balance: Number(wallet.firm_balance),
    lawyer_quota: Number(wallet.lawyer_quota),
    unallocated_balance: Number(wallet.unallocated_balance),
  }));
}

export function listFirmAccounts(): Promise<FirmAccount[]> {
  return request<FirmAccount[]>("/firm/accounts").then((accounts) => accounts.map((account) => ({
    ...account,
    spending_limit: Number(account.spending_limit),
    allocated_amount: Number(account.allocated_amount),
    used_amount: Number(account.used_amount),
  })));
}

export function createFirmAccount(name: string, mobile: string): Promise<FirmAccount & { initial_password: string }> {
  return request("/firm/accounts", { method: "POST", body: JSON.stringify({ name, mobile }) });
}

export function updateFirmAccount(accountId: string, status: "ACTIVE" | "DISABLED"): Promise<FirmAccount> {
  return request(`/firm/accounts/${accountId}`, { method: "PATCH", body: JSON.stringify({ status }) });
}

export function adjustFirmAccountQuota(accountId: string, amountDelta: number): Promise<{ spending_limit: number }> {
  return request(`/firm/accounts/${accountId}/quota-adjustments`, { method: "POST", body: JSON.stringify({ amount_delta: amountDelta, business_reference: crypto.randomUUID() }) });
}

export function resetFirmAccountPassword(accountId: string): Promise<{ initial_password: string }> {
  return request(`/firm/accounts/${accountId}/reset-password`, { method: "POST" });
}

export function createRecharge(amount: number): Promise<{ pay_url: string }> {
  return request("/wallet/recharges", { method: "POST", body: JSON.stringify({ amount }) });
}

export function getPersonalLawyerWallet(rechargePage = 1): Promise<PersonalWallet> {
  return request<PersonalWallet>(`/personal-lawyer/wallet?recharge_page=${rechargePage}`).then((wallet) => ({
    ...wallet,
    total_balance: Number(wallet.total_balance),
    recharge_balance: Number(wallet.recharge_balance),
    reward_balance: Number(wallet.reward_balance),
    available_recharge_channels: wallet.available_recharge_channels,
    recharges: wallet.recharges.map((item) => ({ ...item, amount: Number(item.amount) })),
  }));
}

export function createPersonalLawyerRecharge(amount: number, channel: RechargeChannel): Promise<PersonalRecharge> {
  return request<PersonalRecharge>("/personal-lawyer/wallet/recharges", {
    method: "POST",
    body: JSON.stringify({ amount, channel }),
  }).then((recharge) => ({ ...recharge, amount: Number(recharge.amount) }));
}

export function getPersonalLawyerRecharge(rechargeId: string): Promise<PersonalRecharge> {
  return request<PersonalRecharge>(`/personal-lawyer/wallet/recharges/${encodeURIComponent(rechargeId)}`)
    .then((recharge) => ({ ...recharge, amount: Number(recharge.amount) }));
}

export function createOnlinePayment(orderId: string): Promise<PaymentRecord & { pay_url?: string }> {
  return request("/customer-payments", { method: "POST", body: JSON.stringify({ order_id: orderId, channel: "WECHAT" }) });
}

export function payFromBalance(orderId: string): Promise<PaymentRecord> {
  return request<PaymentRecord>("/payments/balance", {
    method: "POST",
    body: JSON.stringify({ order_id: orderId, idempotency_key: crypto.randomUUID() }),
  });
}

export function submitSupport(orderId: string | undefined, question: string, images: File[]): Promise<SupportTicketDetail> {
  const body = new FormData();
  if (orderId) body.append("order_id", orderId);
  body.append("question", question);
  images.forEach((image) => body.append("images", image));
  return request<SupportTicketDetail>("/support", { method: "POST", body }).then(supportPaths);
}

export function listSupport(): Promise<SupportTicket[]> {
  return request<SupportTicket[]>("/support");
}

export function getSupport(ticketId: string): Promise<SupportTicketDetail> {
  return request<SupportTicketDetail>(`/support/${ticketId}`).then(supportPaths);
}

function supportPaths(detail: SupportTicketDetail): SupportTicketDetail {
  return { ...detail, messages: detail.messages.map(message => ({
    ...message, attachments: message.attachments.map(attachment => ({
      ...attachment, url: appPath(attachment.url),
    })),
  })) };
}

export function replySupport(ticketId: string, message: string, images: File[]): Promise<SupportTicketDetail> {
  const body = new FormData();
  body.append("body", message);
  images.forEach((image) => body.append("images", image));
  return request<SupportTicketDetail>(`/support/${ticketId}/messages`, { method: "POST", body }).then(supportPaths);
}

function normalizeOrder(order: BackendOrder): QueryOrder {
  return {
    id: order.id,
    productCode: order.product_code,
    subjectSummary: order.subject_summary,
    status: order.status,
    salePrice: Number(order.sale_price_snapshot),
    billingPolicyCode: order.billing_policy_code,
    billingStatus: order.billing_status,
    createdAt: order.created_at,
    inquiryPayload: order.inquiry_payload,
    reportExport: order.report_export ? normalizeReportExport(order.report_export) : null,
  };
}

function normalizeReportExport(exportTask: BackendReportExport): ReportExportSummary {
  return {
    id: exportTask.id,
    status: exportTask.status,
    filename: exportTask.filename,
    resultNumber: exportTask.result_number,
    resultVersion: exportTask.result_version,
    canSave: exportTask.can_save,
    errorSummary: exportTask.error_summary,
  };
}

function customerOrderPayload(input: CreateOrderInput): Record<string, unknown> {
  return {
    product_code: input.productCode,
    subject_summary: input.subjectSummary,
    inquiry_payload: input.inquiryPayload,
    ...(input.authorizationMaterialIds?.length
      ? { authorization_material_ids: input.authorizationMaterialIds }
      : {}),
  };
}

export function uploadOrderAuthorizationMaterial(file: File): Promise<OrderAuthorizationMaterial> {
  const body = new FormData();
  body.append("file", file);
  return request<OrderAuthorizationMaterial>("/customer/order-authorizations", {
    method: "POST",
    body,
  });
}

export async function createOrder(input: CreateOrderInput): Promise<QueryOrder> {
  const order = await request<BackendOrder>("/orders", {
    method: "POST",
    body: JSON.stringify({
      product_code: input.productCode,
      subject_summary: input.subjectSummary,
    }),
  });
  return normalizeOrder(order);
}

export async function getOrder(orderId: string): Promise<QueryOrder> {
  return normalizeOrder(await request<BackendOrder>(`/orders/${orderId}`));
}

export async function createCustomerOrder(input: CreateOrderInput): Promise<QueryOrder> {
  const order = await request<BackendOrder>("/customer/orders", {
    method: "POST",
    body: JSON.stringify(customerOrderPayload(input)),
  });
  return normalizeOrder(order);
}

export async function previewCustomerOrder(input: CreateOrderInput): Promise<CustomerOrderQuote> {
  const quote = await request<{
    product_code: QueryOrder["productCode"];
    subject_summary: string;
    sale_price: number;
  }>("/customer/orders/quote", {
    method: "POST",
    body: JSON.stringify(customerOrderPayload(input)),
  });
  return {
    productCode: quote.product_code,
    subjectSummary: quote.subject_summary,
    salePrice: Number(quote.sale_price),
  };
}

export async function payCustomerOrderFromBalance(input: CreateOrderInput): Promise<QueryOrder> {
  const order = await request<BackendOrder>("/customer/orders/balance", {
    method: "POST",
    body: JSON.stringify({
      ...customerOrderPayload(input),
      idempotency_key: crypto.randomUUID(),
    }),
  });
  return normalizeOrder(order);
}

export async function getCustomerOrder(orderId: string): Promise<QueryOrder> {
  return normalizeOrder(await request<BackendOrder>(`/customer/orders/${orderId}`));
}

export async function listOrders(): Promise<QueryOrder[]> {
  return (await request<BackendOrder[]>("/customer/orders")).map(normalizeOrder);
}

export function confirmMockPayment(orderId: string): Promise<PaymentRecord> {
  return request<PaymentRecord>("/payments/mock-confirm", {
    method: "POST",
    body: JSON.stringify({
      order_id: orderId,
      reference: `mock-${crypto.randomUUID()}`,
    }),
  });
}

export function getInquiryResult(orderId: string): Promise<InquiryResultResponse> {
  return request<InquiryResultResponse>(`/customer/orders/${orderId}/result`);
}

export function requestReportExport(orderId: string): Promise<ReportExportSummary> {
  return request<BackendReportExport>(`/customer/orders/${encodeURIComponent(orderId)}/report-export`, {
    method: "POST",
  }).then(normalizeReportExport);
}

export function getReportExport(orderId: string): Promise<ReportExportSummary> {
  return request<BackendReportExport>(`/customer/orders/${encodeURIComponent(orderId)}/report-export`)
    .then(normalizeReportExport);
}

export function saveReportExportUrl(orderId: string): string {
  return appPath(`/api/customer/orders/${encodeURIComponent(orderId)}/report-export/download`);
}
